import React, { forwardRef, useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { display } from "@mui/system";

export const buttonStyle: React.CSSProperties = {
    boxShadow: '10px 6px 10px 0px #486DA5 inset, 0px 4px 4px 0px #00000040',
    borderRadius: '5px', backgroundColor: '#5F84BA', color: 'white',
    border: 'none',
    cursor: 'pointer',
}

export interface MediaMtxHlsVideoProps extends Omit<React.VideoHTMLAttributes<HTMLVideoElement>, "src"> {
    mediaMtxHlsUrl: string,
    additionalButtons?: React.ReactNode
}

const MediaMtxHlsVideo = ({ mediaMtxHlsUrl, additionalButtons, ...props }: MediaMtxHlsVideoProps) => {
    const retryPause = 2000;

    const video = useRef<HTMLVideoElement | null>(null)
    // const message = document.getElementById('message');

    const hlsRef = useRef<Hls | null>(null)
    const [restart, setRestart] = useState<boolean>(false)

    let defaultControls = false;

    const setMessage = (str: string) => {
        if (str !== '') {
            video.current!.controls = false;
        } else {
            video.current!.controls = defaultControls;
        }
        // message.innerText = str;
    };

    const loadStream = () => {
        // always prefer hls.js over native HLS.
        // this is because some Android versions support native HLS
        // but don't support fMP4s.
        if (Hls.isSupported()) {
            const hls = new Hls({
                maxLiveSyncPlaybackRate: 1.5,
                backBufferLength: 0,
            });

            let retryCount = 0
            hls.on(Hls.Events.ERROR, (evt, data) => {
                if (data.fatal) {
                    hls.destroy();

                    if (data.details === 'manifestIncompatibleCodecsError') {
                        setMessage('stream makes use of codecs which are incompatible with this browser or operative system');
                    } else if (data.response && data.response.code === 404) {
                        setMessage('stream not found, retrying in some seconds');
                    } else {
                        setMessage(data.error + ', retrying in some seconds');
                    }

                    setTimeout(() => setRestart(true), retryPause);
                }
            });

            hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                hls.loadSource(mediaMtxHlsUrl + window.location.search);
            });

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                setMessage('');
                video.current!.play();
            });

            hls.attachMedia(video.current!);

            return hls
        } else if (video.current!.canPlayType('application/vnd.apple.mpegurl')) {
            // since it's not possible to detect timeout errors in iOS,
            // wait for the playlist to be available before starting the stream
            fetch(mediaMtxHlsUrl)
                .then(() => {
                    video.current!.src = mediaMtxHlsUrl;
                    video.current!.play();
                });
        }
        return null
    };

    if (restart) {
        hlsRef.current = loadStream()
        setRestart(false)
    }

    const parseBoolString = (str: string, defaultVal: boolean) => {
        str = (str || '');

        if (['1', 'yes', 'true'].includes(str.toLowerCase())) {
            return true;
        }
        if (['0', 'no', 'false'].includes(str.toLowerCase())) {
            return false;
        }
        return defaultVal;
    }

    const loadAttributesFromQuery = () => {
        const params = new URLSearchParams(mediaMtxHlsUrl);
        video.current!.controls = parseBoolString(params?.get('controls') ?? "", false);
        video.current!.muted = parseBoolString(params?.get('muted') ?? "", true);
        video.current!.autoplay = parseBoolString(params?.get('autoplay') ?? "", true);
        video.current!.playsInline = parseBoolString(params?.get('playsinline') ?? "", true);
        defaultControls = video.current!.controls;
    }

    const lock = useRef({ state: false })
    const init = () => {
        if (!lock.current.state) {
            lock.current.state = true
            loadAttributesFromQuery();
            hlsRef.current = loadStream()
        }

        return () => {
            lock.current.state = false
            hlsRef.current?.detachMedia()
            hlsRef.current?.stopLoad()
            hlsRef.current?.destroy()
        }
    }

    useEffect(init, [mediaMtxHlsUrl])

    return (
        <>
            <video
                ref={
                    (node) => {
                        video.current = node;
                    }}
                {...props}>
            </video>
            <div style={{ display: "flex", justifyContent: "end", gap: "10px", }}>
                {additionalButtons}
                <button
                    style=
                    {
                        {
                            ...buttonStyle,
                            fontFamily: 'Faune', width: 'fit-content', fontSize: '18px',
                            fontWeight: '400', marginTop: '10px', paddingLeft: '15px', paddingRight: '15px',
                            display: 'block', justifySelf: 'end'
                        }
                    }
                    onClick={() => { video.current?.requestFullscreen() }}
                >
                    Full Screen
                </button>
            </div>
        </>
    )
}

export default MediaMtxHlsVideo